/**
 * What a URL says in words, and what it does not.
 *
 * One rule for every place that turns a URL into a query or a redirect:
 * - middleware turns other stores' search links into searches
 *   (`SEARCH_ROUTE_WORDS`, `searchLinkWords`);
 * - the not-found page prefills (and for a human, runs) the words of a dead
 *   path (`deadUrlToQuery`, through `parseDeadPath`);
 * - the no-products page asks the search for suggestions (`getRecoveryOptions`);
 * - a one-segment URL with no page is looked up only when it is a word
 *   (`isWordSegment`, in server/not-found/resolve-dead-slug.ts).
 *
 * Before this module each of those read the deepest path segment as the query
 * with no check, so `/en-US/s?k=air+fryer` searched "S", an Amazon product URL
 * searched its ASIN, and crawler probes (`wp-admin`, `index.php`) were searched
 * and redirected like product names (flow-dead-search-links-lose-the-query).
 *
 * Pure and dependency-free: middleware runs on the Edge runtime and the error
 * boundaries that call parseDeadPath are client components.
 */

/**
 * Other stores' search routes. Middleware answers `/{locale}/{word}` and
 * everything below it with a search, before any page lookup, so no page may
 * ever live there (a test guards the app route folders; the url_mappings side
 * is checked by scripts/qa-2026-09-28/deadlinks-search-route-precheck.ts).
 */
import { formatId } from 'shared/src/utils/format-id';

export const SEARCH_ROUTE_WORDS = ['s', 'search', 'recherche', 'buscar'] as const;

/** Query-string keys a search link carries its words in, first non-empty wins. */
export const SEARCH_QUERY_KEYS = ['k', 'field-keywords', 'q', 'query', 'keywords'] as const;

/** Longest query a search link hands the search bar, in characters. */
export const MAX_SEARCH_LINK_WORDS = 200;

/**
 * Path segments that name a route, not something to shop for: other stores'
 * routes (Amazon's `/dp/`, `/gp/product/`, `/stores/…/page/…`, `/b?node=`,
 * `/s?k=`), cart and account routes, and the paths crawlers probe. Never a
 * query, never looked up. Compared lowercase. Segments shorter than three
 * characters are refused anyway (see `isWordSegment`); the short ones stay
 * listed so the list reads as the contract it is.
 */
export const FOREIGN_ROUTE_WORDS: ReadonlySet<string> = new Set([
  ...SEARCH_ROUTE_WORDS,
  // Other stores' routes
  'b',
  'e',
  'dp',
  'gp',
  'sspa',
  'stores',
  'store',
  'shop',
  'shops',
  'product',
  'products',
  'category',
  'categories',
  'ref',
  'node',
  'page',
  'pages',
  'exec',
  'obidos',
  'offer-listing',
  'product-reviews',
  'customer-reviews',
  'wishlist',
  // Cart and account routes
  'cart',
  'basket',
  'checkout',
  'login',
  'logout',
  'signin',
  'sign-in',
  'signup',
  'sign-up',
  'register',
  'account',
  'accounts',
  'my-account',
  'myaccount',
  'profile',
  'user',
  'users',
  // Paths crawlers probe
  'admin',
  'administrator',
  'wp-admin',
  'wp-login',
  'wp-content',
  'wp-includes',
  'wp-json',
  'xmlrpc',
  'cgi-bin',
  'phpmyadmin',
  'index',
  'api',
  'graphql',
  'rss',
]);

/** A file name, not a word: `index.php`, `Beacon2.jpg`, `config.js`, `sitemap.xml`. */
const FILE_NAME =
  /\.(?:php\d?|phtml|aspx?|jsp|cgi|pl|py|rb|sh|html?|xml|json|txt|csv|js|mjs|cjs|css|map|env|ini|cfg|conf|config|ya?ml|toml|log|sql|db|bak|old|orig|swp|zip|gz|tgz|tar|rar|7z|jpe?g|png|gif|webp|avif|svg|ico|bmp|tiff?|pdf|mp3|mp4|webm|mov|woff2?|ttf|otf|eot)$/i;

/** An Amazon product id (`B07FDJMC9Q`). */
const ASIN = /^B0[0-9A-Z]{8}$/i;

/** An ISBN-10 or ISBN-13, which Amazon uses as the id of a book. */
const ISBN = /^(?:97[89])?\d{9}[\dX]$/i;

/** Fewer letters and digits than this is not a query (`s`, `e`, `b`, `dp`); `ps5` is. */
const MIN_WORD_CHARACTERS = 3;

/** The segment's text, or null when it is not valid percent-encoding (never words). */
const decodeSegment = (segment: string): string | null => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
};

/**
 * Whether one path segment is words a shopper could have meant. Refuses route
 * words, product and book ids, all-digit and hex ids, anything holding `=`
 * (`ref=nb_sb_noss`), file names, malformed escapes, and segments with fewer
 * than three letters and digits. Model names such as `ps5` pass.
 */
export const isWordSegment = (segment: string): boolean => {
  const word = decodeSegment(segment ?? '')?.trim();
  if (!word) return false;
  if (word.includes('=')) return false;
  if (word.startsWith('.') || FILE_NAME.test(word)) return false;
  if (FOREIGN_ROUTE_WORDS.has(word.toLowerCase())) return false;
  if (ASIN.test(word) || ISBN.test(word)) return false;
  const compact = word.toLowerCase().replace(/[-_]/g, '');
  if (/^\d+$/.test(compact)) return false;
  if (compact.length >= 8 && /^[0-9a-f]+$/.test(compact) && /\d/.test(compact) && /[a-f]/.test(compact)) {
    return false;
  }
  return (word.match(/[\p{L}\p{N}]/gu) ?? []).length >= MIN_WORD_CHARACTERS;
};

/**
 * "wireless-noise-cancelling-headphones" → "Wireless Noise Cancelling Headphones".
 * A job or mapping id ends in its locale ("…-hair-fr-CA"); those two segments
 * are not words (they read "Fr Ca", 2026-10-13), so formatId drops them first.
 */
export const slugToSearchTerm = (slug: string): string =>
  formatId(slug)
    .split(/[-_+\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * The words a dead path stands for: its deepest word segment, or '' when it
 * has none. The leaf of a catalogue path carries the specific name and its
 * parents are context, so the search starts from the deepest word; route
 * words, ids and file names above or below it are skipped.
 *
 *   ['Air-Fryer-Ninja', 'dp', 'B07FDJMC9Q'] → 'Air Fryer Ninja'
 *   ['gp', 'product', 'B07PMLV6LD']         → ''
 *   ['table-tournante-vinyle']              → 'Table Tournante Vinyle'
 */
export const deadUrlToQuery = (categories: string[]): string => {
  for (let index = categories.length - 1; index >= 0; index -= 1) {
    const segment = categories[index];
    if (isWordSegment(segment)) return slugToSearchTerm(decodeSegment(segment) ?? segment);
  }
  return '';
};

/**
 * The words a search link carries: the first non-empty of
 * `k`, `field-keywords`, `q`, `query`, `keywords`. URLSearchParams has already
 * turned `+` into a space; whitespace is collapsed, control characters dropped
 * and the result capped at MAX_SEARCH_LINK_WORDS characters. A value with no
 * letter or digit is no query.
 */
export const searchLinkWords = (params: URLSearchParams): string => {
  for (const key of SEARCH_QUERY_KEYS) {
    for (const value of params.getAll(key)) {
      const words = value
        .replace(/[\u0000-\u001f\u007f]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      if (!/[\p{L}\p{N}]/u.test(words)) continue;
      // By code point, so a cut never splits a character in two.
      return Array.from(words).slice(0, MAX_SEARCH_LINK_WORDS).join('').trim();
    }
  }
  return '';
};
